import { type FC } from 'react';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';

// MUI
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { BarChart } from '@mui/x-charts/BarChart';

// screen
import { buildDailySeries } from 'modules/dashboard/helpers/accessStats';
import type { AccessStatisticsResponse } from 'modules/dashboard/types';

interface Props {
  data?: AccessStatisticsResponse | null;
}

const BAR = '#C9DAFF';
const BAR_TODAY = '#2F6BFF';

/**
 * Tầng 3: số lượt theo từng ngày trong tháng.
 *
 * Cột hôm nay được tô đậm bằng MỘT SERIES RIÊNG chỉ có giá trị ở đúng vị trí đó (các ô khác null →
 * không vẽ). Cách này dùng API chắc chắn có ở @mui/x-charts v7, không phụ thuộc colorMap.
 */
const AccessDailyChart: FC<Props> = ({ data }) => {
  const { formatMessage } = useIntl();
  // M1: BE gộp ngày theo UTC+7 (giờ VN), không phải giờ máy người xem. `dayjs().format(...)` trước
  // đây lấy ngày LOCAL của trình duyệt — người xem ở múi giờ khác VN sẽ tô sai cột "hôm nay" (lệch
  // ngày) hoặc không tô cột nào cả. Cộng thẳng 7 giờ vào giờ UTC hiện tại để luôn ra đúng ngày theo
  // lịch VN bất kể múi giờ máy người xem là gì. Dùng plugin `utc` đã được đăng ký sẵn ở App.tsx nên
  // không cần thêm dependency hay cấu hình mới.
  const todayKey = dayjs().utc().add(7, 'hour').format('YYYY-MM-DD');
  const { labels, values, todayIndex } = buildDailySeries(data?.daily, todayKey);

  // I7: @mui/x-charts v7 xếp các series KHÔNG stack cạnh nhau trong cùng một band, nên nếu để cả 2
  // series đều có giá trị tại todayIndex thì mỗi ngày (kể cả những ngày khác) đều bị chia đôi bề
  // rộng cột (hẹp, có khe), và riêng ngày hôm nay vẽ ra 2 cột cạnh nhau (nhạt + đậm) thay vì 1 cột
  // đậm duy nhất. Sửa bằng cách: (1) loại todayIndex khỏi series nền (giá trị null tại đó — x-charts
  // không vẽ ô null), và (2) gán CHUNG một `stack` id cho cả hai series để x-charts luôn coi mỗi
  // ngày chỉ có MỘT cột chồng (nền hoặc đậm, không bao giờ cả hai cùng lúc vì luôn có đúng 1 bên null).
  const backgroundValues = values.map((v, i) => (i === todayIndex ? null : v));
  const todayValues = values.map((v, i) => (i === todayIndex ? v : null));

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2.5,
        border: '1px solid #EEF0F4',
        boxShadow: '0 2px 10px rgba(16,24,40,0.06)',
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        {formatMessage({ id: 'dashboard.access.chartTitle' })}
      </Typography>

      {labels.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.disabled', py: 4, textAlign: 'center' }}>
          {formatMessage({ id: 'dashboard.noData' })}
        </Typography>
      ) : (
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <BarChart
            height={300}
            xAxis={[
              {
                scaleType: 'band',
                data: labels,
                label: formatMessage({ id: 'dashboard.access.chartDayAxis' }),
              },
            ]}
            yAxis={[{ label: formatMessage({ id: 'dashboard.access.chartValueAxis' }) }]}
            series={[
              { data: backgroundValues, color: BAR, stack: 'day' },
              { data: todayValues, color: BAR_TODAY, stack: 'day' },
            ]}
            slotProps={{ legend: { hidden: true } }}
          />
        </Box>
      )}
    </Paper>
  );
};

export default AccessDailyChart;
